import { Heading, Text, ThemeProps } from '@hhgtech/hhg-components'
import BreadcrumbMarryBaby from 'components/atoms/breadcrumb'
import {
  SectionContainerStyled,
  StyledContainer,
} from 'components/atoms/container/index.styled'
import HeaderDescription from 'components/templates/HeaderDescription.template'
import { domainLocales } from 'domainLocales'
import { HealthTool } from 'interfaces/types'
import { useRouter } from 'next/router'
import ArrowRightIcon from 'public/svg/arrow-right.svg'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { deviceLayout } from 'styles/breakpoint'

const HealthToolsContainerStyled = styled(SectionContainerStyled)`
  .header-description {
    ${deviceLayout.spLayout`
      background-color: ${(props: ThemeProps) =>
        props.theme.mbColors.lightPink};
      border-radius: 0% 0% 50% 50% / 0% 0% 5% 5%;      
    `}
  }
  .container-description {
    display: flex;
    flex-direction: column;
    ${deviceLayout.pcLayout`
        flex-direction: unset;
    `}
    .title {
      font-weight: 600;
    }
    .content {
      padding: 16px 0;
    }
    .container-banner {
      ${deviceLayout.pcLayout`
        width: 30%;
      `}
    }
    .container-info {
      margin-top: 40px;
    }
  }

  .container-main {
    display: flex;
    flex-wrap: wrap;
    margin-top: 24px;
    .wrapper-card {
      padding: 12px 0px;
      ${deviceLayout.pcLayout`
        width: 50%;
        padding: 12px;
        margin: 0;
      `}
    }
  }
`

const CardStyled = styled(SectionContainerStyled)`
  display: flex;
  box-sizing: border-box;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  border: 1px solid ${(props: ThemeProps) => props.theme.mbColors.lightGray};
  background: ${(props: ThemeProps) => props.theme.colors.white};
  border-radius: 16px;
  box-shadow: 0px 1px 3px rgba(0, 0, 0, 0.08);
  cursor: pointer;
  .card-direct {
    text-align: right;
    svg path {
      stroke: #3e3f58;
    }
  }
  .card-title {
    display: flex;
    align-items: center;
    .card-content {
      padding-left: 16px;
      font-weight: 600;
    }
    .card-icon {
      display: flex;
      width: 64px;
      height: 64px;
      align-items: center;
      justify-content: center;
      background: #f3f3f3;
      border-radius: 99px;
      svg {
        max-width: 32px;
        max-height: 32px;
      }
    }
  }
`

interface IHealthToolCard {
  content: string
  icon: JSX.Element
  href: string
}

const HealthToolCard = ({ content, icon, href }: IHealthToolCard) => (
  <a href={href}>
    <CardStyled className="container-card">
      <section className="card-title">
        <section className="card-icon">{icon}</section>
        <section
          style={{
            width: 'calc(100% - 64px)',
          }}
        >
          <Text size="p3" color="#3E3F58" className="card-content">
            {content}
          </Text>
        </section>
      </section>
      <section className="card-direct">
        <ArrowRightIcon />
      </section>
    </CardStyled>
  </a>
)

const HealthTools = (props: { healthTools: HealthTool[] }) => {
  const { formatMessage: f } = useIntl()
  const { locale } = useRouter() || {}
  const sL = locale || 'vi-VN' // standardized locale
  const breadcrumbMarryBabyProps = {
    pages: [
      {
        label: f({ id: 'healthtools.title' }),
        value: '/health-tools',
        dataEventCategory: 'Breadcrumb',
        dataEventAction: 'Click',
        dataEventLabel: `https://${domainLocales[sL]}/health-tools`,
      },
    ],
  }

  const cards = props.healthTools.map((tool) => ({
    icon: (
      <img
        loading="lazy"
        src={tool.icon}
        alt={tool.title}
        style={{ width: 32, height: 32, objectFit: 'cover' }}
      />
    ),
    content: tool.title,
    href: tool.link,
  }))

  return (
    <HealthToolsContainerStyled>
      <HeaderDescription>
        <StyledContainer className="container-header">
          <BreadcrumbMarryBaby {...breadcrumbMarryBabyProps} />
          <SectionContainerStyled
            as="article"
            className="container-description"
          >
            <SectionContainerStyled className="container-info">
              <Heading tag="h1" as="h1" color="#202135" className="title">
                {f({ id: 'healthtools.title' })}
              </Heading>
              <Text size="p3" color="#3E3F58" className="content">
                {f({
                  id: 'healthtools.homeDescription',
                  defaultMessage:
                    'These easy-to-use health assessment tools will help you gauge vital parts of your overall health and wellness.',
                })}
              </Text>
            </SectionContainerStyled>
            <SectionContainerStyled className="container-banner">
              <img loading="lazy" src="/svg/health-tools/banner.svg" />
            </SectionContainerStyled>
          </SectionContainerStyled>
        </StyledContainer>
      </HeaderDescription>
      <StyledContainer as="main" className="container-main">
        {cards.map((card) => (
          <SectionContainerStyled
            key={card.content as string}
            className="wrapper-card"
          >
            <HealthToolCard {...card} />
          </SectionContainerStyled>
        ))}
      </StyledContainer>
    </HealthToolsContainerStyled>
  )
}
export default HealthTools
